import React, { useEffect, useRef, useState } from "react";
import { colonyApi } from "@/lib/colonyApi";
import { TutorialButton } from "@/components/tutorials/TutorialButton";
import { InvoiceAttachments } from "@/components/finance/InvoiceAttachments";
import { ColonyStatementTotals } from "@/components/finance/ColonyStatementTotals";
import { ColonyInvoiceFilters } from "@/components/finance/ColonyInvoiceFilters";
import { filterColonyStatementInvoices } from "@/lib/colonyInvoiceFilters";
import { useCalendarPage } from "@/lib/useCalendarPage";
import { CalendarYearSelect } from "@/components/CalendarYearSelect";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AiBreakdownPanel } from "@/components/finance/AiBreakdownPanel";
import { toast } from "sonner";
import { Loader2, Receipt, FileText, ChevronDown, ChevronRight, Download, Sparkles } from "lucide-react";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { isPdfMedia } from "@/lib/livePhotoMedia";
import "./ColonyStatement.css";

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString() : "");

const StatusChip = ({ status, testId }) => {
  const cls = status === "paid"
    ? "bg-emerald-100 text-emerald-800"
    : status === "sent"
      ? "bg-sky-100 text-sky-800"
      : status === "partial"
        ? "bg-amber-100 text-amber-800"
      : "bg-secondary text-muted-foreground";
  return <span data-testid={testId} className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold uppercase tracking-wider ${cls}`}>{status === "paid" ? "Paid" : status === "sent" ? "Due" : status === "partial" ? "Partial" : "Draft"}</span>;
};

export default function ColonyStatement() {
  const [colonies, setColonies] = useState([]);
  const [cid, setCid] = useState("");
  const statementPage = useCalendarPage({ client: colonyApi, path: cid ? `/colony/statement/${cid}` : "", enabled: !!cid });
  const stmt = statementPage.data;
  const [coloniesLoading, setLoading] = useState(true);
  const loading = coloniesLoading || statementPage.loading;
  const viewKey = useRef(""); viewKey.current = `${cid}:${statementPage.requested}:${statementPage.year}`;
  const [openInv, setOpenInv] = useState(null);
  const [invoiceStatus, setInvoiceStatus] = useState("outstanding");
  const [invoiceType, setInvoiceType] = useState("all");
  const [downloading, setDownloading] = useState(null);
  // Collapse toggles for the two long lists at the bottom of the page.
  // Expanded by default so the user still sees everything on first load.
  // Session-only — no localStorage — because the colony picker resets the
  // sections' relevance anyway.
  const [showCharges, setShowCharges] = useState(true);
  const [showPayments, setShowPayments] = useState(true);
  const [receiptView, setReceiptView] = useState(null); // { url, alt }
  const [openExp, setOpenExp] = useState(null); // expanded un-invoiced charge id
  const [lineAi, setLineAi] = useState({}); // expense_id -> "loading" | {ai_audit, has_receipt}

  const aiEnabled = !!stmt?.colony?.show_ai_breakdown;

  const openExpenseReceipt = async (eid) => {
    const key = viewKey.current;
    try {
      const r = await colonyApi.get(`/colony/expenses/${eid}/receipts`);
      const imgs = r.data?.receipts || [];
      if (imgs.length && key === viewKey.current) setReceiptView({ imgs, alt: "receipt" });
    } catch { /* toggle raced off — nothing useful to show */ }
  };

  const toggleLineAi = async (eid) => {
    const key = viewKey.current;
    if (lineAi[eid] && lineAi[eid] !== "loading") {
      setLineAi((p) => ({ ...p, [eid]: undefined }));
      return;
    }
    setLineAi((p) => ({ ...p, [eid]: "loading" }));
    try {
      const r = await colonyApi.get(`/colony/expenses/${eid}/ai-breakdown`);
      if (key === viewKey.current) setLineAi((p) => ({ ...p, [eid]: r.data }));
    } catch {
      setLineAi((p) => ({ ...p, [eid]: undefined }));
      toast.error("Couldn't load the details for this charge.");
    }
  };

  const openReceipt = async (invId, expenseId) => {
    const key = viewKey.current;
    try {
      const r = await colonyApi.get(`/colony/invoices/${invId}/lines/${expenseId}/receipts`);
      const imgs = r.data?.receipts || [];
      if (!imgs.length || key !== viewKey.current) return;
      setReceiptView({ imgs, alt: "receipt" });
    } catch {
      // silent — the FE only offers this on markup-free invoices; a 403
      // means something race-y happened, e.g. the admin added a markup
      // moments before the click. Nothing useful to show the user.
    }
  };

  const downloadPdf = async (inv) => {
    setDownloading(inv.id);
    try {
      const r = await colonyApi.get(`/colony/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.number || "invoice"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* toast not available here; button label resets */ }
    finally { setDownloading(null); }
  };

  useEffect(() => {
    colonyApi.get("/colony/me")
      .then((r) => {
        const list = r.data?.colonies || [];
        setColonies(list);
        if (list.length) setCid(list[0].id);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    setOpenInv(null); setOpenExp(null); setReceiptView(null); setLineAi({});
  }, [cid, statementPage.requested, statementPage.year]);

  const t = stmt?.totals || {};
  const visibleInvoices = filterColonyStatementInvoices(stmt?.invoices, invoiceStatus, invoiceType);

  useEffect(() => { setOpenInv(null); }, [invoiceStatus, invoiceType]);

  return (
    <div className="space-y-4" data-testid="colony-statement-page">
      {statementPage.error && <div role="alert" className="text-sm text-destructive" data-testid="colony-statement-year-error">{statementPage.error} <Button variant="outline" size="sm" onClick={statementPage.reload} data-testid="colony-statement-year-retry">Retry</Button></div>}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3"><h1 className="font-display text-3xl font-bold tracking-tight text-emerald-900">Statement</h1><TutorialButton audience="colony" section="statement" /></div>
          <p className="text-sm text-muted-foreground">Invoices and charges billed to your colony, and payments received.</p>
        </div>
        <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2" data-testid="colony-statement-top-actions">
        {cid && <CalendarYearSelect page={statementPage} id="colony-statement-year" />}
        {colonies.length > 1 && (
          <select
            value={cid}
            onChange={(e) => setCid(e.target.value)}
            className="h-8 max-w-full rounded-sm border border-border bg-background px-2 text-xs"
            data-testid="colony-statement-picker"
          >
            {colonies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : !stmt && statementPage.error ? null : !stmt ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">No statement available yet.</Card>
      ) : (
        <>
          {t.billing_basis === "sent_invoices" ? <ColonyStatementTotals statement={t} prefix="colony-stmt" /> : <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Card className="p-4"><div className="text-xs uppercase tracking-wide text-muted-foreground">Total charges</div><div className="text-2xl font-bold" data-testid="colony-stmt-charges">{fmtMoney(t.billed_total)}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase tracking-wide text-muted-foreground">Payments received</div><div className="text-2xl font-bold text-emerald-700">{fmtMoney(t.received_total)}</div></Card>
            <Card className="p-4 col-span-2 sm:col-span-1"><div className="text-xs uppercase tracking-wide text-muted-foreground">Balance due</div><div className="text-2xl font-bold text-destructive" data-testid="colony-stmt-balance">{fmtMoney(t.balance)}</div></Card>
          </div>}

            <Card className="overflow-hidden" data-testid="colony-statement-invoices">
              <div className="colony-invoice-toolbar px-3 py-2 sm:px-4 border-b" data-testid="colony-invoice-filters">
                <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-700" /><h2 className="font-semibold" data-testid="colony-statement-invoices-title">Invoices</h2></div>
                <ColonyInvoiceFilters status={invoiceStatus} type={invoiceType} onStatusChange={setInvoiceStatus} onTypeChange={setInvoiceType} />
              </div>
              {visibleInvoices.length === 0 && (
                <p className="px-4 py-6 text-sm text-muted-foreground" role="status" data-testid="colony-invoice-filter-empty">
                  No {invoiceStatus === "outstanding" ? "outstanding " : ""}{invoiceType === "all" ? "" : `${invoiceType} `}invoices for this year.
                </p>
              )}
              <div className="divide-y" data-testid="colony-invoice-filter-results">
                {visibleInvoices.map((inv) => {
                  const isOpen = openInv === inv.id;
                  const fullyPaid = Number(inv.amount_paid || 0) > 0 && Number(inv.balance_due ?? inv.total) <= 0.005;
                  const partial = Number(inv.amount_paid || 0) > 0 && Number(inv.balance_due ?? inv.total) > 0.005;
                  const displayStatus = fullyPaid ? "paid" : partial ? "partial" : inv.status;
                  return (
                    <div key={inv.id} data-testid={`colony-stmt-invoice-${inv.id}`}>
                      <button
                        type="button"
                        onClick={() => setOpenInv(isOpen ? null : inv.id)}
                        className="colony-invoice-row w-full px-3 sm:px-4 py-2.5 text-sm text-left hover:bg-secondary/30"
                        aria-expanded={isOpen}
                        data-testid={`colony-stmt-invoice-toggle-${inv.id}`}
                      >
                        <div className="colony-invoice-arrow text-muted-foreground">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</div>
                        <div className="colony-invoice-number font-semibold" data-testid={`colony-invoice-number-${inv.id}`}>{inv.number}</div>
                        <div className="colony-invoice-date text-muted-foreground" data-testid={`colony-invoice-date-${inv.id}`}>{fmtDate(inv.invoice_date)}</div>
                        <div className="colony-invoice-status"><StatusChip status={displayStatus} testId={`colony-invoice-status-${inv.id}`} /></div>
                        <div className="colony-invoice-amount text-right"><div className="font-semibold" data-testid={`colony-invoice-due-${inv.id}`}>Due {fmtMoney(inv.balance_due ?? inv.total)}</div>{Number(inv.amount_paid || 0) > 0 && <div className="text-[10px] text-emerald-700" data-testid={`colony-invoice-paid-${inv.id}`}>Paid {fmtMoney(inv.amount_paid)}</div>}</div>
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-3 bg-secondary/20" data-testid={`colony-stmt-invoice-lines-${inv.id}`}>
                          <div className="border border-border rounded-sm bg-background divide-y">
                            {(inv.line_items || []).map((li, i) => (
                              <div key={i}>
                                <div className="px-3 py-1.5 flex items-center justify-between gap-3 text-sm">
                                  <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                                    <span className="text-muted-foreground text-xs">{fmtDate(li.date)}</span>
                                    {li.category && (
                                      <span className="text-[10px] uppercase tracking-wider bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded-sm font-semibold shrink-0">
                                        {li.category}
                                      </span>
                                    )}
                                    <span className="truncate">
                                      {li.description}{li.vendor ? ` · ${li.vendor}` : ""}
                                    </span>
                                    {/* Receipt lives inside AI details; colonies without AI details keep the plain link.
                                        Only present on markup-free invoices (backend strips it otherwise). */}
                                    {!(aiEnabled && li.ai_scanned) && li.has_receipt && li.expense_id && (
                                      <button
                                        type="button"
                                        onClick={() => openReceipt(inv.id, li.expense_id)}
                                        className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-800 hover:text-emerald-900 underline underline-offset-2 shrink-0"
                                        title="Open the attached receipt"
                                        data-testid={`colony-invoice-line-receipt-${inv.id}-${i}`}
                                      >
                                        <FileText className="w-3 h-3" /> Receipt
                                      </button>
                                    )}
                                    {aiEnabled && li.ai_scanned && li.expense_id && (
                                      <button
                                        type="button"
                                        onClick={() => toggleLineAi(li.expense_id)}
                                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-violet-700 hover:text-violet-900 underline underline-offset-2 shrink-0"
                                        title="See what the AI read off this receipt"
                                        data-testid={`colony-invoice-line-ai-${inv.id}-${i}`}
                                      >
                                        <Sparkles className="w-3 h-3" /> {lineAi[li.expense_id] === "loading" ? "…" : "AI details"}
                                      </button>
                                    )}
                                  </div>
                                  <div className="font-medium shrink-0">{fmtMoney(li.amount)}</div>
                                </div>
                                {lineAi[li.expense_id] && lineAi[li.expense_id] !== "loading" && (
                                  <div className="px-3 pb-2">
                                    <AiBreakdownPanel
                                      audit={lineAi[li.expense_id].ai_audit}
                                      hasReceipt={!!(li.has_receipt && li.expense_id)}
                                      onViewReceipt={() => openReceipt(inv.id, li.expense_id)}
                                      testId={`colony-invoice-line-ai-panel-${inv.id}-${i}`}
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                            <div className="px-3 py-1.5 flex justify-between text-sm font-bold">
                              <span>Invoice total</span><span>{fmtMoney(inv.total)}</span>
                            </div>
                            {Number(inv.amount_paid || 0) > 0 && <div className="px-3 py-1.5 flex justify-between text-sm text-emerald-700"><span>Payments received</span><span>−{fmtMoney(inv.amount_paid)}</span></div>}
                            <div className="px-3 py-1.5 flex justify-between text-sm font-bold border-t"><span>Still unpaid</span><span>{fmtMoney(inv.balance_due ?? inv.total)}</span></div>
                          </div>
                          {inv.notes && <p className="text-xs text-muted-foreground mt-2">{inv.notes}</p>}
                          {String(stmt.colony?.ledger || "").startsWith("mgmt_") && <InvoiceAttachments key={inv.id} invoiceId={inv.id} viewer="colony" refreshKey={inv.attachment_version || 0} />}
                          <div className="flex justify-end mt-2">
                            <button
                              type="button"
                              onClick={() => downloadPdf(inv)}
                              disabled={downloading === inv.id}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 rounded-sm px-2.5 py-1.5 disabled:opacity-50"
                              data-testid={`colony-invoice-pdf-${inv.id}`}
                            >
                              <Download className="w-3.5 h-3.5" /> {downloading === inv.id ? "Preparing…" : "Download PDF"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

          <Card className="overflow-hidden">
            <button
              type="button"
              onClick={() => setShowCharges((v) => !v)}
              className="w-full px-4 py-3 border-b flex items-center gap-2 hover:bg-secondary/40 transition-colors text-left"
              aria-expanded={showCharges}
              data-testid="colony-stmt-toggle-charges"
            >
              <Receipt className="w-4 h-4 text-emerald-700" />
              <h2 className="font-semibold flex-1">Charges not yet invoiced</h2>
              <span className="text-xs text-muted-foreground mr-1">
                {(stmt.expenses || []).length + (stmt.payroll_billing || []).length}
              </span>
              {showCharges
                ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </button>
            {showCharges && (
              (stmt.expenses || []).length === 0 && (stmt.payroll_billing || []).length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">No open charges.</p>
              ) : (
                <div className="divide-y">
                  {stmt.expenses.map((e) => {
                    const expandable = aiEnabled && (e.ai_audit || e.has_receipt);
                    const isOpen = openExp === e.id;
                    return (
                      <div key={e.id} data-testid={`colony-stmt-expense-${e.id}`}>
                        <div
                          className={`grid grid-cols-12 px-4 py-2.5 items-center text-sm ${expandable ? "cursor-pointer hover:bg-secondary/30" : ""}`}
                          onClick={expandable ? () => setOpenExp(isOpen ? null : e.id) : undefined}
                          role={expandable ? "button" : undefined}
                          data-testid={expandable ? `colony-stmt-expense-toggle-${e.id}` : undefined}
                        >
                          <div className="col-span-3 text-muted-foreground flex items-center gap-1">
                            {expandable && (isOpen ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />)}
                            <span>{fmtDate(e.date)}</span>
                          </div>
                          <div className="col-span-6">
                            <div className="font-medium flex items-center gap-1.5 flex-wrap">
                              {e.category || "Charge"}
                              {expandable && e.ai_audit && (
                                <span className="text-[9px] px-1 py-0.5 rounded-sm bg-violet-100 text-violet-800 font-semibold uppercase tracking-wider inline-flex items-center gap-0.5"><Sparkles className="w-2.5 h-2.5" /> AI</span>
                              )}
                            </div>
                            {e.notes && <div className="text-xs text-muted-foreground">{e.notes}</div>}
                          </div>
                          <div className="col-span-3 text-right font-semibold">{fmtMoney(e.amount)}</div>
                        </div>
                        {isOpen && (
                          <div className="px-4 pb-3">
                            <AiBreakdownPanel
                              audit={e.ai_audit}
                              hasReceipt={e.has_receipt}
                              onViewReceipt={() => openExpenseReceipt(e.id)}
                              testId={`colony-stmt-expense-ai-${e.id}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(stmt.payroll_billing || []).map((charge) => (
                    <div key={charge.id} className="grid grid-cols-12 px-4 py-2.5 items-center text-sm bg-emerald-50/50" data-testid={`colony-stmt-payroll-billing-${charge.id}`}>
                      <div className="col-span-3 text-muted-foreground">{fmtDate(charge.date)}</div>
                      <div className="col-span-6">
                        <div className="font-medium">Payroll services</div>
                        <div className="text-xs text-muted-foreground">{charge.period_start} → {charge.period_end}</div>
                      </div>
                      <div className="col-span-3 text-right font-semibold">{fmtMoney(charge.amount)}</div>
                    </div>
                  ))}
                </div>
              )
            )}
          </Card>

          <Card className="overflow-hidden">
            <button
              type="button"
              onClick={() => setShowPayments((v) => !v)}
              className="w-full px-4 py-3 border-b flex items-center gap-2 hover:bg-secondary/40 transition-colors text-left"
              aria-expanded={showPayments}
              data-testid="colony-stmt-toggle-payments"
            >
              <FileText className="w-4 h-4 text-emerald-700" />
              <h2 className="font-semibold flex-1">Payments received</h2>
              <span className="text-xs text-muted-foreground mr-1">
                {(stmt.payments || []).length}
              </span>
              {showPayments
                ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </button>
            {showPayments && (
              (stmt.payments || []).length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">No payments recorded yet.</p>
              ) : (
                <div className="divide-y">
                  {stmt.payments.map((p) => (
                    <div key={p.id} className="grid grid-cols-12 px-4 py-2.5 items-center text-sm">
                      <div className="col-span-3 text-muted-foreground">{fmtDate(p.paid_at)}</div>
                      <div className="col-span-6">{p.note || p.payment_method || "Payment"}</div>
                      <div className="col-span-3 text-right font-semibold text-emerald-700">{fmtMoney(p.amount)}</div>
                    </div>
                  ))}
                </div>
              )
            )}
          </Card>
        </>
      )}

      {/* Colony-side receipt viewer. Only reachable on markup-free
          invoices — the backend refuses when markup_amount > 0. */}
      {receiptView && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center p-4 gap-3"
          onClick={() => setReceiptView(null)}
          role="dialog"
          data-testid="colony-receipt-lightbox"
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white text-2xl leading-none px-2 py-0.5"
            onClick={() => setReceiptView(null)}
            aria-label="Close"
            data-testid="colony-receipt-lightbox-close"
          >
            ×
          </button>
          <div
            className="max-w-4xl max-h-[85vh] w-full overflow-auto space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            {receiptView.imgs.map((src, i) => (
              isPdfMedia(src) || /\.pdf(\?|$)/i.test(src) ? (
                <iframe key={i} src={src} className="w-full h-[80vh] bg-white rounded-sm" title={`receipt-${i}`} />
              ) : (
                <LivePhotoSurface key={i} value={src} alt={receiptView.alt} className="max-w-full mx-auto rounded-sm bg-white" imageClassName="object-contain" testId={`colony-receipt-media-${i}`} eagerMotion />
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
